import type { ReactNode } from "react";

import { cn } from "../../../lib/utils";

export type VersionSourceTone = "generated" | "manual" | "ai" | "restored";

export interface VersionEntry {
  id: string;
  version: number;
  createdAt: string;
  createdBy?: string | null;
  /** Secondary attribute that distinguishes this version, e.g. the model it was saved with. */
  meta?: string;
  /**
   * Set when this version already holds what the record currently contains.
   * Reverting to it would write a duplicate, so the action is not offered.
   */
  isCurrentState?: boolean;
  /** Source tag such as Generated / Manual / AI edit / Restored. */
  sourceBadge?: string | null;
  /** Color treatment for `sourceBadge` on the script-history rail. */
  sourceTone?: VersionSourceTone;
  /** Short write-time change label shown on the rail. */
  changeLabel?: string | null;
  /** Extra detail such as an AI prompt. */
  detail?: string | null;
  /** Preformatted relative time; rail falls back to a compact clock if omitted. */
  createdAtLabel?: string | null;
}

export interface VersionTimelineLabels {
  latest?: string;
  revert?: string;
  empty?: string;
  /** Prefixed to the version number to build each row's accessible name. */
  select?: string;
  /** Heading for versions saved on the current date. */
  today?: string;
}

export interface VersionTimelineProps {
  /** List of versions, ordered newest first */
  versions: VersionEntry[];
  /** Currently selected version ID */
  activeVersionId?: string;
  /** Called when a version is selected for viewing */
  onSelect?: (version: VersionEntry) => void;
  /** Called when user clicks revert on a non-latest version */
  onRevert?: (version: VersionEntry) => void;
  /** Translated copy — consumers pass strings from their i18n layer. */
  labels?: VersionTimelineLabels;
  /**
   * `default` is the prompt-editor list (day groups).
   * `rail` is the Opportunities V2 script-history timeline (dots + source pills).
   */
  variant?: "default" | "rail";
  /**
   * Rail only. Rendered inside the active card, below its select button, so a card-scoped
   * control (e.g. a toggle) does not nest inside the button.
   */
  activeFooter?: ReactNode;
  /** Additional class names */
  className?: string;
}

const RAIL_CHIP_CLASS =
  "inline-flex max-w-full items-center whitespace-normal rounded-full border px-1.5 py-0.5 text-[9.5px] font-medium leading-none";

const SLATE_CHIP_CLASS =
  "border-transparent bg-[var(--slate-chip-bg)] text-[var(--slate-chip-text)]";

const SOURCE_TONE_CLASS: Record<VersionSourceTone, string> = {
  generated: SLATE_CHIP_CLASS,
  // Canvas fill so the pill still reads on an active mint row.
  manual: "border-ai/20 bg-background text-ai-text",
  ai: "border-transparent bg-[var(--ai-fill-chip)] text-[var(--ai-text)]",
  restored: "border-transparent bg-warning-subtle text-warning",
};

function isSameDay(a: Date, b: Date): boolean {
  return a.toDateString() === b.toDateString();
}

/** Drops the year in the current year — the panel is too narrow to spend width on it. */
function formatDay(iso: string, todayLabel: string): string {
  const date = new Date(iso);
  const now = new Date();
  if (isSameDay(date, now)) return todayLabel;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatCompactDateTime(iso: string): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return iso;
  const now = new Date();
  const time = then.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (isSameDay(then, now)) return time;
  return then.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    ...(then.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
  });
}

function formatFullDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

interface VersionRow {
  entry: VersionEntry;
  isLatest: boolean;
  /**
   * Model and author are shown only on the version that introduced them. A history
   * where one person saved eight versions against one model has one thing to say,
   * not twenty-four, and the rows that do differ are what the eye should catch.
   */
  changes: string[];
}

interface DayGroup {
  key: string;
  label: string;
  rows: VersionRow[];
}

function groupByDay(versions: VersionEntry[], todayLabel: string): DayGroup[] {
  const groups: DayGroup[] = [];

  versions.forEach((entry, idx) => {
    const older = versions[idx + 1];
    const changes: string[] = [];
    if (entry.meta && entry.meta !== older?.meta) changes.push(entry.meta);
    if (entry.createdBy && entry.createdBy !== older?.createdBy) {
      changes.push(entry.createdBy);
    }

    const row: VersionRow = { entry, isLatest: idx === 0, changes };
    const key = new Date(entry.createdAt).toDateString();
    const current = groups[groups.length - 1];

    if (current?.key === key) current.rows.push(row);
    else {
      groups.push({ key, label: formatDay(entry.createdAt, todayLabel), rows: [row] });
    }
  });

  return groups;
}

export function VersionTimeline({
  versions,
  activeVersionId,
  onSelect,
  onRevert,
  labels,
  variant = "default",
  activeFooter,
  className,
}: VersionTimelineProps) {
  const latestLabel = labels?.latest ?? "Latest";
  const revertLabel = labels?.revert ?? "Revert";
  const emptyLabel = labels?.empty ?? "No versions yet.";
  const selectLabel = labels?.select ?? "Select version";
  const todayLabel = labels?.today ?? "Today";

  if (versions.length === 0) {
    return (
      <div className={cn("py-6 text-center text-sm text-muted-foreground", className)}>
        {emptyLabel}
      </div>
    );
  }

  if (variant === "rail") {
    return (
      <RailTimeline
        versions={versions}
        activeVersionId={activeVersionId}
        onSelect={onSelect}
        latestLabel={latestLabel}
        selectLabel={selectLabel}
        activeFooter={activeFooter}
        className={className}
      />
    );
  }

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {groupByDay(versions, todayLabel).map((group) => (
        <section key={group.key}>
          <h4 className="px-2 pb-1 text-[11px] text-muted-foreground">{group.label}</h4>
          <ol className="flex flex-col gap-1">
            {group.rows.map(({ entry, isLatest, changes }) => {
              const isActive = entry.id === activeVersionId;
              const canRevert = !isLatest && !entry.isCurrentState && Boolean(onRevert);

              return (
                <li key={entry.id} className="group relative">
                  <button
                    type="button"
                    onClick={() => onSelect?.(entry)}
                    aria-current={isActive ? "true" : undefined}
                    aria-label={`${selectLabel} ${entry.version}`}
                    className={cn(
                      "flex w-full cursor-pointer flex-col items-start gap-0.5 rounded-md px-2 py-1.5 text-left",
                      "transition-colors duration-150 ease-out",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
                      isActive
                        ? "bg-primary-subtle"
                        : // `muted` is #f9fafb — a 1% gray that vanishes against the
                          // panel, and vanishes completely at /60. Mixing the
                          // foreground instead gives a hover that is perceptible on
                          // either theme without competing with the selected row.
                          "hover:bg-[color-mix(in_srgb,var(--foreground)_6%,transparent)]",
                    )}
                  >
                    {/* Only this line shares the row with Latest / Revert, so the
                        reservation stops here and the change line keeps full width. */}
                    <span className="flex w-full items-baseline gap-2 pr-12">
                      <span
                        className={cn(
                          "text-[13px] font-medium tabular-nums tracking-[-0.01em]",
                          isActive
                            ? "text-primary-subtle-foreground"
                            : "text-foreground",
                        )}
                      >
                        v{entry.version}
                      </span>
                      <span
                        title={formatFullDate(entry.createdAt)}
                        className={cn(
                          "text-[11px] tabular-nums",
                          isActive
                            ? "text-primary-subtle-foreground/70"
                            : "text-muted-foreground",
                        )}
                      >
                        {formatTime(entry.createdAt)}
                      </span>
                    </span>
                    {entry.sourceBadge ? (
                      <span
                        className={cn(
                          "text-[11px] font-medium",
                          isActive
                            ? "text-primary-subtle-foreground/85"
                            : "text-foreground/80",
                        )}
                      >
                        {entry.sourceBadge}
                      </span>
                    ) : null}
                    {entry.changeLabel ? (
                      <span
                        className={cn(
                          "block w-full truncate text-[11px]",
                          isActive
                            ? "text-primary-subtle-foreground/85"
                            : "text-muted-foreground",
                        )}
                      >
                        {entry.changeLabel}
                      </span>
                    ) : null}
                    {entry.detail ? (
                      <span
                        title={entry.detail}
                        className={cn(
                          "block w-full truncate text-[11px] italic",
                          isActive
                            ? "text-primary-subtle-foreground/70"
                            : "text-muted-foreground",
                        )}
                      >
                        {entry.detail}
                      </span>
                    ) : null}
                    {entry.createdBy && (entry.sourceBadge || entry.changeLabel) ? (
                      <span
                        className={cn(
                          "block w-full truncate text-[11px]",
                          isActive
                            ? "text-primary-subtle-foreground/70"
                            : "text-muted-foreground",
                        )}
                      >
                        {entry.createdBy}
                      </span>
                    ) : null}
                    {changes.length > 0 && !entry.sourceBadge && !entry.changeLabel && (
                      <span
                        title={changes.join(" · ")}
                        className={cn(
                          "block w-full truncate text-[11px]",
                          isActive
                            ? "text-primary-subtle-foreground/85"
                            : "text-muted-foreground",
                        )}
                      >
                        {changes.join(" · ")}
                      </span>
                    )}
                  </button>

                  {isLatest && (
                    <span
                      className={cn(
                        "pointer-events-none absolute right-2 top-1 flex h-6 items-center text-[11px]",
                        isActive
                          ? "text-primary-subtle-foreground/70"
                          : "text-muted-foreground",
                      )}
                    >
                      {latestLabel}
                    </span>
                  )}

                  {canRevert && (
                    <button
                      type="button"
                      onClick={() => onRevert?.(entry)}
                      className={cn(
                        // foreground/70 rather than muted-foreground: it must clear 4.5:1 on
                        // the plain row and on the tinted selected row alike.
                        "absolute right-1 top-1 flex h-6 cursor-pointer items-center rounded-sm px-1.5 text-[11px] font-medium text-foreground/70",
                        "transition-[opacity,background-color,color] duration-150 ease-out",
                        "hover:bg-background hover:text-foreground",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
                        // Secondary action stays out of the way until the row is engaged.
                        "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100",
                        "[@media(hover:none)]:opacity-100",
                      )}
                    >
                      {revertLabel}
                    </button>
                  )}
                </li>
              );
            })}
          </ol>
        </section>
      ))}
    </div>
  );
}

function RailTimeline({
  versions,
  activeVersionId,
  onSelect,
  latestLabel,
  selectLabel,
  activeFooter,
  className,
}: {
  versions: VersionEntry[];
  activeVersionId?: string;
  onSelect?: (version: VersionEntry) => void;
  latestLabel: string;
  selectLabel: string;
  activeFooter?: ReactNode;
  className?: string;
}) {
  return (
    <ol className={cn("flex flex-col", className)}>
      {versions.map((entry, idx) => {
        const isActive = entry.id === activeVersionId;
        const isLatest = idx === 0;
        const showConnector = idx < versions.length - 1;
        const when = entry.createdAtLabel ?? formatCompactDateTime(entry.createdAt);

        return (
          <li key={entry.id} className="flex gap-[11px]">
            <div className="flex flex-none flex-col items-center">
              <span
                className={cn(
                  "mt-2 size-[11px] rounded-full border-2",
                  isActive
                    ? "border-ai bg-ai"
                    : "border-border bg-background",
                )}
                aria-hidden
              />
              {showConnector ? (
                <span className="min-h-[22px] w-px flex-1 bg-border" aria-hidden />
              ) : null}
            </div>
            <div
              className={cn(
                "mb-2.5 min-w-0 flex-1 rounded-sm border",
                "transition-colors duration-150 ease-out",
                isActive
                  ? "border-ai bg-ai-subtle"
                  : "border-transparent hover:bg-[color-mix(in_srgb,var(--foreground)_6%,transparent)]",
              )}
            >
              <button
                type="button"
                onClick={() => onSelect?.(entry)}
                aria-current={isActive ? "true" : undefined}
                aria-label={`${selectLabel} ${entry.version}`}
                className="block w-full min-w-0 cursor-pointer rounded-sm px-2.5 py-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
              >
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[13px] font-semibold tabular-nums tracking-[-0.01em] text-foreground">
                    v{entry.version}
                  </span>
                  {entry.sourceBadge ? (
                    <span
                      className={cn(
                        RAIL_CHIP_CLASS,
                        isActive && entry.sourceTone
                          ? SOURCE_TONE_CLASS[entry.sourceTone]
                          : SLATE_CHIP_CLASS,
                      )}
                    >
                      {entry.sourceBadge}
                    </span>
                  ) : null}
                  {isLatest ? (
                    <span
                      className={cn(
                        RAIL_CHIP_CLASS,
                        isActive
                          ? "border-ai/20 bg-background text-ai-text"
                          : SLATE_CHIP_CLASS,
                      )}
                    >
                      {latestLabel}
                    </span>
                  ) : null}
                </span>
                {entry.changeLabel ? (
                  <span className="mt-1 block wrap-anywhere text-[11.5px] leading-snug text-foreground">
                    {entry.changeLabel}
                  </span>
                ) : null}
                {entry.detail ? (
                  <span className="mt-0.5 block wrap-anywhere text-[11px] italic leading-snug text-[var(--ai-text)]">
                    “{entry.detail}”
                  </span>
                ) : null}
                <span className="mt-1 block wrap-anywhere text-[10.5px] text-muted-foreground">
                  {entry.createdBy ? `${when} · ${entry.createdBy}` : when}
                </span>
              </button>
              {isActive && activeFooter ? (
                <div className="border-t border-ai/20 px-2.5 py-1.5">{activeFooter}</div>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
